import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import Icon from '@components/atoms/icon';
import Slider from '@components/atoms/slider';
import { triggerToast } from '@components/atoms/toast';
import { TeamBadge } from '@components/transfers/TeamBadge';
import { useSolverProfiles } from '@hooks/transfers/useResolvedPreferences';
import {
  CUSTOM_PROFILE_ID,
  type SolverConfiguration
} from '@lib/transfers/store/preferences-slice';
import { useAITransfersStore } from '@lib/transfers/store/useAITransfersStore';
import {
  applyConfigurationCommit,
  CONFIGURATION_KEYS,
  SLIDER_GROUPS,
  type ConfigurationSliderDefinition,
  type SolverConfigurationKey
} from '@lib/transfers/utils/solver-configuration';
import {
  CUSTOM_PROFILE_LABEL,
  profileLabelFor
} from '@lib/transfers/utils/profile-label';

type ProfileCardProps = {
  label: string;
  description: string;
  isSelected: boolean;
  onSelect: () => void;
  hasEditIcon?: boolean;
};

const ProfileCard = ({
  label,
  description,
  isSelected,
  onSelect,
  hasEditIcon = false
}: ProfileCardProps) => (
  <button
    type="button"
    role="radio"
    aria-checked={isSelected}
    onClick={onSelect}
    className={`grid cursor-pointer grid-cols-[20px_1fr] items-start gap-x-3 gap-y-1 rounded-xl p-[13px] text-left transition-colors duration-150 md:p-3.5 ${
      isSelected
        ? 'bg-primary-50/50 ring-[1.5px] ring-inset ring-primary-600'
        : 'bg-white ring-1 ring-inset ring-neutral-200 hover:bg-neutral-100'
    }`}
    data-cy={`ai-profile-card-${label.toLowerCase()}`}
  >
    <span
      className={`mt-px flex h-[18px] w-[18px] items-center justify-center rounded-full ${
        isSelected
          ? 'bg-primary-600'
          : 'bg-white ring-2 ring-inset ring-neutral-300'
      }`}
      aria-hidden
    >
      {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
    </span>
    <span className="inline-flex items-center gap-1.5 font-open-sans text-[15px] font-bold leading-[1.35] text-neutral-800">
      {label}
      {hasEditIcon && (
        <span className="text-neutral-600">
          <Icon name="Edit" fontSize="14px" aria-hidden />
        </span>
      )}
    </span>
    <span
      className={`col-start-2 font-open-sans text-[13px] leading-[1.55] ${
        isSelected ? 'text-neutral-700' : 'text-neutral-600'
      }`}
    >
      {description}
    </span>
  </button>
);

type SliderRowProps = {
  definition: ConfigurationSliderDefinition;
  value: number | undefined;
  isEdited: boolean;
  onValueCommit: (value: number) => void;
};

const SliderRow = ({
  definition,
  value,
  isEdited,
  onValueCommit
}: SliderRowProps) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-baseline justify-between gap-3">
      <span className="inline-flex items-center gap-1.5 font-open-sans text-sm font-bold text-neutral-800">
        {definition.title}
        {isEdited && (
          <span className="flex-shrink-0 rounded-md bg-primary-50 px-1.5 py-px font-open-sans text-[10px] font-bold uppercase tracking-[0.08em] text-primary-700">
            edited
          </span>
        )}
      </span>
      {value !== undefined && (
        <span className="rounded-lg bg-primary-600 px-2.5 py-0.5 font-open-sans text-[13px] font-bold tabular-nums text-white">
          {definition.formatValue(value)}
        </span>
      )}
    </div>
    <Slider
      variant="controlled"
      name={`${definition.key}-slider`}
      min={definition.min}
      max={definition.max}
      step={definition.step}
      value={value === undefined ? undefined : [value]}
      onValueCommit={(committed) =>
        onValueCommit(Array.isArray(committed) ? committed[0] : committed)
      }
      fixedLabels={false}
      shouldHideThumbLabel
    />
    <div className="flex items-center justify-between font-open-sans text-[11px] text-neutral-600">
      <span>{definition.lowAnchor}</span>
      <span>{definition.highAnchor}</span>
    </div>
    <p className="mb-0 font-open-sans text-xs leading-[1.5] text-neutral-600">
      {definition.hint}
    </p>
  </div>
);

type AIProfilePanelProps = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * The AI profile side panel (bottom sheet on mobile): pick which profile
 * drives the AI transfer recommendations, or tune a custom configuration.
 * Profiles come from the backend; the choice persists via the AI transfers
 * store and flows into the recommendations query through
 * useResolvedPreferences.
 */
const AIProfilePanel = ({ isOpen, onClose }: AIProfilePanelProps) => {
  const { data } = useSolverProfiles();

  const selectedProfileId = useAITransfersStore(
    (state) => state.selectedProfileId
  );
  const customConfiguration = useAITransfersStore(
    (state) => state.customConfiguration
  );
  const setPreferences = useAITransfersStore((state) => state.setPreferences);
  const blacklistedPlayers = useAITransfersStore(
    (state) => state.blacklistedPlayers
  );
  const removeBlacklistedPlayer = useAITransfersStore(
    (state) => state.removeBlacklistedPlayer
  );

  const profiles = data?.data ?? [];
  const defaultProfile =
    profiles.find((profile) => profile.isDefault) ?? profiles[0] ?? null;

  const [draftProfileName, setDraftProfileName] = useState('');
  const [draftConfiguration, setDraftConfiguration] =
    useState<SolverConfiguration | null>(null);
  // The preset Custom was seeded from; "Reset values" restores it.
  const [originPresetName, setOriginPresetName] = useState<string | null>(null);
  // Avoided players staged for removal; committed on apply like the sliders.
  const [draftRemovedAvoidIds, setDraftRemovedAvoidIds] = useState<string[]>(
    []
  );

  // Re-seed the drafts from the saved selection each time the panel opens so
  // an abandoned edit never leaks into the next visit. The origin preset is
  // not persisted, so a saved Custom selection falls back to the default
  // profile as its reset target.
  useEffect(() => {
    if (!isOpen) return;
    setDraftProfileName(selectedProfileId ?? defaultProfile?.name ?? '');
    setDraftConfiguration(
      customConfiguration ?? defaultProfile?.configuration ?? null
    );
    setOriginPresetName(
      selectedProfileId === CUSTOM_PROFILE_ID
        ? defaultProfile?.name ?? null
        : null
    );
    setDraftRemovedAvoidIds([]);
  }, [isOpen, selectedProfileId, customConfiguration, defaultProfile]);

  if (profiles.length === 0 || !defaultProfile) {
    return null;
  }

  const isCustomSelected = draftProfileName === CUSTOM_PROFILE_ID;
  const activePresetProfile =
    profiles.find((profile) => profile.name === draftProfileName) ??
    defaultProfile;
  const originPresetProfile =
    profiles.find((profile) => profile.name === originPresetName) ?? null;

  // The preset the Custom card's description names: the one Custom is (or
  // would be) seeded from.
  const customSeedProfile = isCustomSelected
    ? originPresetProfile ?? defaultProfile
    : activePresetProfile;

  const draftLabel = isCustomSelected
    ? CUSTOM_PROFILE_LABEL
    : profileLabelFor(activePresetProfile.name);

  // Saved state the change count compares against; a never-saved selection
  // means the backend default.
  const savedProfileId = selectedProfileId ?? defaultProfile.name;
  const savedConfiguration =
    savedProfileId === CUSTOM_PROFILE_ID
      ? customConfiguration
      : profiles.find((profile) => profile.name === savedProfileId)
          ?.configuration ?? defaultProfile.configuration;

  // Buy-side blacklist entries are the "avoided" players; sell-side entries
  // don't exist today but the filter keeps this honest if they ever do.
  const avoidedPlayers = blacklistedPlayers.filter(
    (player) => player.type === 'in'
  );
  const visibleAvoidedPlayers = avoidedPlayers.filter(
    (player) => !draftRemovedAvoidIds.includes(player.id)
  );
  const avoidRemovalCount =
    avoidedPlayers.length - visibleAvoidedPlayers.length;

  const isProfileChanged = draftProfileName !== savedProfileId;
  const valueChangeCount =
    isCustomSelected && draftConfiguration && savedConfiguration
      ? CONFIGURATION_KEYS.filter(
          (key) => draftConfiguration[key] !== savedConfiguration[key]
        ).length
      : 0;
  const changeCount =
    valueChangeCount + avoidRemovalCount + (isProfileChanged ? 1 : 0);
  const hasChanges = changeCount > 0;

  const handleProfileChange = (nextProfileName: string) => {
    if (nextProfileName === CUSTOM_PROFILE_ID && !isCustomSelected) {
      // Custom starts from the saved custom configuration, falling back to
      // the preset the member was just looking at; remember that preset so
      // "Reset values" has a target.
      setDraftConfiguration(
        customConfiguration ?? activePresetProfile.configuration
      );
      setOriginPresetName(activePresetProfile.name);
    }
    setDraftProfileName(nextProfileName);
  };

  const handleSliderCommit = (key: SolverConfigurationKey, value: number) => {
    setDraftConfiguration((previous) =>
      previous ? applyConfigurationCommit(previous, key, value) : previous
    );
  };

  const handleResetValues = () => {
    if (!originPresetProfile) return;
    setDraftConfiguration(originPresetProfile.configuration);
  };

  const handleApply = () => {
    setPreferences(
      draftProfileName,
      isCustomSelected ? draftConfiguration : null
    );
    draftRemovedAvoidIds.forEach((id) => removeBlacklistedPlayer(id, 'in'));
    const hasProfileEdits = isProfileChanged || valueChangeCount > 0;
    triggerToast({
      title: 'Success',
      subTitle: hasProfileEdits
        ? `AI profile updated to ${draftLabel}`
        : 'Avoided players updated',
      variant: 'success'
    });
    onClose();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1000] bg-neutral-800/45 data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
        <Dialog.Content
          className="fixed inset-x-0 bottom-0 z-[10000] flex max-h-[88vh] flex-col rounded-t-[20px] bg-white shadow-500 focus:outline-none data-[state=open]:animate-slide-in-bottom data-[state=closed]:animate-slide-out-bottom md:inset-x-auto md:bottom-0 md:right-0 md:top-0 md:h-full md:max-h-none md:w-[480px] md:rounded-none md:data-[state=open]:animate-slide-in-right md:data-[state=closed]:animate-slide-out-right"
          data-cy="ai-profile-panel"
        >
          <div
            className="mx-auto mt-2 h-1 w-10 flex-shrink-0 rounded-full bg-neutral-300 md:hidden"
            aria-hidden
          />

          <header className="flex items-start justify-between gap-3 border-b border-neutral-200 px-5 pb-4 pt-5">
            <div className="flex flex-col gap-0.5">
              <Dialog.Title asChild>
                <h2 className="mb-0 font-sans text-lg font-semibold text-neutral-800 md:text-[20px]">
                  AI profile
                </h2>
              </Dialog.Title>
              <Dialog.Description asChild>
                <p className="mb-0 font-open-sans text-xs text-neutral-600 md:text-[13px]">
                  Drives every transfer and lineup recommendation.
                </p>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-neutral-700 transition-colors duration-150 hover:bg-neutral-200"
                data-cy="ai-profile-close"
              >
                <Icon name="Close" fontSize="18px" />
              </button>
            </Dialog.Close>
          </header>

          <div className="flex flex-1 flex-col gap-[18px] overflow-y-auto px-4 py-3.5 md:px-5 md:py-4">
            <div
              role="radiogroup"
              aria-label="AI profile"
              className="flex flex-col gap-2"
            >
              {profiles.map((profile) => (
                <ProfileCard
                  key={profile.name}
                  label={profileLabelFor(profile.name)}
                  description={profile.description}
                  isSelected={draftProfileName === profile.name}
                  onSelect={() => handleProfileChange(profile.name)}
                />
              ))}
              <ProfileCard
                label={CUSTOM_PROFILE_LABEL}
                description={`Fine-tune the settings yourself, starting from ${profileLabelFor(
                  customSeedProfile.name
                )}.`}
                isSelected={isCustomSelected}
                onSelect={() => handleProfileChange(CUSTOM_PROFILE_ID)}
                hasEditIcon
              />
            </div>

            {isCustomSelected && (
              <>
                {originPresetProfile && (
                  <div className="flex items-center justify-between gap-3 rounded-lg bg-neutral-100 px-3 py-[9px]">
                    <p className="mb-0 font-open-sans text-[13px] text-neutral-600">
                      Started from{' '}
                      <span className="font-bold text-neutral-800">
                        {profileLabelFor(originPresetProfile.name)}
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={handleResetValues}
                      className="font-open-sans text-[13px] font-bold text-primary-600 transition-colors duration-150 hover:text-primary-700"
                      data-cy="ai-profile-reset-values"
                    >
                      Reset values
                    </button>
                  </div>
                )}

                {SLIDER_GROUPS.map((group) => (
                  <section key={group.heading} className="flex flex-col gap-3">
                    <h3 className="mb-0 font-open-sans text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-600">
                      {group.heading}
                    </h3>
                    {group.sliders.map((definition) => (
                      <SliderRow
                        key={definition.key}
                        definition={definition}
                        value={draftConfiguration?.[definition.key]}
                        isEdited={
                          draftConfiguration !== null &&
                          originPresetProfile !== null &&
                          draftConfiguration[definition.key] !==
                            originPresetProfile.configuration[definition.key]
                        }
                        onValueCommit={(value) =>
                          handleSliderCommit(definition.key, value)
                        }
                      />
                    ))}
                  </section>
                ))}
              </>
            )}

            <section
              className="flex flex-col gap-2.5"
              data-cy="avoided-players-section"
            >
              <div className="flex flex-col gap-0.5">
                <h3 className="mb-0 font-open-sans text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-600">
                  Avoided players
                </h3>
                <p className="mb-0 font-open-sans text-xs leading-[1.5] text-neutral-600">
                  The AI won&apos;t recommend buying these players. Remove one
                  to let them back into future runs.
                </p>
              </div>
              {visibleAvoidedPlayers.length === 0 ? (
                <p
                  className="mb-0 rounded-lg bg-neutral-100 px-3 py-[9px] font-open-sans text-[13px] text-neutral-600"
                  data-cy="avoided-players-empty"
                >
                  {avoidRemovalCount > 0
                    ? 'All avoided players will be restored when you apply.'
                    : 'No avoided players yet. Use "Don\'t recommend" on a transfer suggestion to avoid a player.'}
                </p>
              ) : (
                <ul
                  className="mb-0 flex list-none flex-wrap gap-2 p-0"
                  data-cy="avoided-players-list"
                >
                  {visibleAvoidedPlayers.map((player) => (
                    <li key={player.id}>
                      <span
                        className="inline-flex h-8 items-center gap-[7px] whitespace-nowrap rounded-full bg-white pl-2 pr-1.5 ring-1 ring-inset ring-neutral-200"
                        data-cy="avoided-player-chip"
                      >
                        <TeamBadge
                          shortName={player.team}
                          size="xs"
                          className="flex-shrink-0"
                        />
                        <span className="font-open-sans text-[13px] font-semibold text-neutral-800">
                          {player.displayName}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setDraftRemovedAvoidIds((previous) => [
                              ...previous,
                              player.id
                            ])
                          }
                          aria-label={`Remove ${player.displayName} from avoided players`}
                          className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-neutral-200 text-neutral-600 transition-colors duration-150 hover:bg-neutral-300"
                          data-cy="avoided-player-remove"
                        >
                          <Icon name="Close" fontSize="10px" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <footer className="flex flex-col gap-2.5 border-t border-neutral-200 bg-white px-5 py-3.5 md:flex-row md:items-center md:justify-between md:gap-3">
            <span
              className="font-open-sans text-[13px] text-neutral-600"
              data-cy="ai-profile-change-count"
            >
              {hasChanges
                ? `${changeCount} change${changeCount === 1 ? '' : 's'}`
                : 'No changes to apply'}
            </span>
            <button
              type="button"
              onClick={handleApply}
              disabled={!hasChanges}
              className="min-h-[48px] w-full rounded-lg bg-primary-600 px-[22px] font-sans text-[15px] font-semibold text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-neutral-300 md:min-h-[44px] md:w-auto"
              data-cy="ai-profile-apply"
            >
              <span className="md:hidden">Apply</span>
              <span className="hidden md:inline">Apply &amp; re-run</span>
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AIProfilePanel;
